"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/header";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { signUpFormSchema, type SignUpFormSchema } from "@/schemas/auth-forms";
import { api } from "@/trpc/client";
import { tRPCErrorToUseFormErrors } from "@/utils/misc";

export const SignUpForm = ({
  onSignUpSuccess,
}: {
  onSignUpSuccess?: (token: string) => void;
}) => {
  const signUpForm = useForm<SignUpFormSchema>({
    resolver: zodResolver(signUpFormSchema, { async: true }),
  });

  async function onSignUpSubmit(values: SignUpFormSchema) {
    try {
      const result = await api.auth.signUp.mutate(values);
      if (result.success === true) {
        onSignUpSuccess?.(result.token);
      }
    } catch (error) {
      tRPCErrorToUseFormErrors<typeof signUpForm>(error).forEach((error) => {
        signUpForm.setError(...error);
      });
    }
  }

  return (
    <>
      <PageHeader
        heading={"Sign up to Lighthouse"}
        text={"Create an account to get started with Lighthouse"}
      />
      <Form {...signUpForm}>
        <form
          onSubmit={signUpForm.handleSubmit(onSignUpSubmit)}
          className="m-auto space-y-5 lg:w-11/12"
        >
          <FormField
            control={signUpForm.control}
            name="name"
            key="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="email"
            key="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter your e-mail"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="password"
            key="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={signUpForm.control}
            name="confirmPassword"
            key="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password again"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="md:text-md text-center text-sm">
            By signing up, you agree to our{" "}
            <a
              href="https://www.cloudify.biz/terms-and-conditions"
              className="text-ring"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://www.cloudify.biz/privacy-policy"
              className="text-ring"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </p>
          <Button
            type="submit"
            className="w-full"
            disabled={signUpForm.formState.isSubmitting}
          >
            {signUpForm.formState.isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Sign Up
          </Button>
          {signUpForm.formState.errors.root?.message && (
            <Alert
              variant="destructive"
              id="root-error-message"
              className="mx-auto"
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              <AlertTitle>
                {signUpForm.formState.errors.root?.message}
              </AlertTitle>
            </Alert>
          )}
        </form>
      </Form>
    </>
  );
};
